import { Loader2, ImagePlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { uploadImage } from "@/lib/api";
import { useSettingsForm } from "@/hooks/useSettingsForm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

export const AdminSettings = () => {
  const { form, update, save, saving } = useSettingsForm();
  const [uploading, setUploading] = useState(false);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      update("logo_url", url);
    } catch (err) {
      toast.error("Gagal mengunggah logo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-ink tracking-tight mb-1">Pengaturan Brand</h1>
      <p className="text-stone2 text-sm mb-6">
        Nama usaha, tagline, kontak, dan nomor WhatsApp yang dipakai di seluruh situs.
      </p>

      <div className="rounded-2xl bg-white border border-border/60 p-6 space-y-5 max-w-2xl">
        <div>
          <Label className="mb-2 block">Logo</Label>
          <div className="flex items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-border/60 bg-cream-200 shrink-0">
              <Logo src={form.logo_url} size={40} />
            </div>
            <Input
              value={form.logo_url}
              onChange={(e) => update("logo_url", e.target.value)}
              placeholder="URL logo, atau unggah file"
              className="flex-1"
            />
            <label className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input cursor-pointer hover:bg-accent shrink-0">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} />
            </label>
          </div>
          <p className="text-stone2 text-xs mt-2">
            Kosongkan untuk memakai lambang bawaan (hexagon kuning).
          </p>
        </div>
        <div>
          <Label className="mb-2 block">Nama Usaha</Label>
          <Input value={form.brand_name} onChange={(e) => update("brand_name", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Tagline</Label>
          <Input value={form.tagline} onChange={(e) => update("tagline", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Lokasi Singkat (mis. Jombang, Jawa Timur)</Label>
          <Input value={form.location_text} onChange={(e) => update("location_text", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Email</Label>
          <Input value={form.email} onChange={(e) => update("email", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Nomor WhatsApp (format internasional, tanpa +, mis. 6281234567890)</Label>
          <Input
            data-testid="settings-whatsapp-input"
            value={form.whatsapp_number}
            onChange={(e) => update("whatsapp_number", e.target.value)}
          />
        </div>

        <div className="pt-2 border-t border-border/60">
          <h2 className="font-display text-lg text-ink tracking-tight mb-1">Marketplace & Sosial Media</h2>
          <p className="text-stone2 text-sm mb-4">
            Kosongkan field yang belum punya link — tombol/ikonnya otomatis tidak tampil di halaman publik.
          </p>
        </div>
        <div>
          <Label className="mb-2 block">Link Shopee</Label>
          <Input value={form.shopee_url} onChange={(e) => update("shopee_url", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Link Tokopedia</Label>
          <Input value={form.tokopedia_url} onChange={(e) => update("tokopedia_url", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Link TikTok Shop</Label>
          <Input value={form.tiktok_shop_url} onChange={(e) => update("tiktok_shop_url", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Link Instagram</Label>
          <Input value={form.instagram_url} onChange={(e) => update("instagram_url", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Link Facebook</Label>
          <Input value={form.facebook_url} onChange={(e) => update("facebook_url", e.target.value)} />
        </div>
        <div>
          <Label className="mb-2 block">Link TikTok</Label>
          <Input value={form.tiktok_url} onChange={(e) => update("tiktok_url", e.target.value)} />
        </div>

        <Button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          className="rounded-full bg-honey hover:bg-brandRed text-ink hover:text-cream font-semibold"
        >
          {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </div>
  );
};
